// Furniture: drops PixelLab sprites at staged positions in the room.
// Returns the collision rectangles and a map of interaction points.
import { PALETTE as C } from "./palette.js";
import { TILE, ROOM_W, ROOM_H } from "./room.js";

// Helper: place a sprite if the texture loaded, else fall back to a
// graphics-drawn block so the scene still works when assets fail.
function placeSprite(scene, key, x, y, w, h, fallbackFn) {
  if (scene.textures.exists(key)) {
    const s = scene.add.image(x, y, key);
    s.setDisplaySize(w, h);
    return s;
  }
  return fallbackFn(scene);
}

export function drawFurniture(scene) {
  const collisions = [];
  const interactions = {};

  // ═══ Bar counter (8 tiles wide, 2 tiles deep) ═══
  const barCx = TILE * 10;
  const barCy = TILE * 3.5;
  const barW = TILE * 8;
  const barH = TILE * 1.5;
  placeSprite(scene, "bar", barCx, barCy, barW, barH, (s) => {
    const g = s.add.graphics();
    g.fillStyle(C.wood, 1);
    g.fillRect(barCx - barW / 2, barCy - barH / 2, barW, barH);
    return g;
  });
  collisions.push({ x: barCx - barW / 2, y: barCy - barH / 2, w: barW, h: barH });

  // Ledger sits on the bar (little interaction marker on top)
  const ledgerX = barCx + barW * 0.35;
  const ledgerY = barCy - barH * 0.1;
  // Small decorative highlight for the ledger location
  scene.add.rectangle(ledgerX, ledgerY, 10, 7, C.cream, 0.4).setStrokeStyle(1, C.gold, 0.6);
  interactions.ledger = {
    x: ledgerX,
    y: barCy + barH / 2 + TILE * 0.6,
    radius: TILE * 1.2,
    label: "Read the ledger",
    slug: "ledger"
  };

  // ═══ Hearth (3 tiles wide, 3 tiles tall) ═══
  const hearthX = ROOM_W - TILE * 2.5;
  const hearthY = TILE * 4.5;
  const hearthW = TILE * 2.5;
  const hearthH = TILE * 3;
  placeSprite(scene, "hearth", hearthX, hearthY, hearthW, hearthH, (s) => {
    const g = s.add.graphics();
    g.fillStyle(C.stoneDark, 1);
    g.fillRect(hearthX - hearthW / 2, hearthY - hearthH / 2, hearthW, hearthH);
    return g;
  });
  collisions.push({ x: hearthX - hearthW / 2, y: hearthY - hearthH / 2, w: hearthW, h: hearthH });
  interactions.hearth = {
    x: hearthX,
    y: hearthY + hearthH / 2 + TILE * 0.8,
    radius: TILE * 2.2,
    label: "Warm yourself by the hearth",
    slug: "hearth"
  };

  // ═══ Three tables (each 1.5 tiles) ═══
  const tables = [
    { x: TILE * 4.5,  y: TILE * 7 },
    { x: TILE * 10,   y: TILE * 8 },
    { x: TILE * 15.5, y: TILE * 7 },
  ];
  for (const t of tables) {
    const tW = TILE * 1.6;
    const tH = TILE * 1.6;
    placeSprite(scene, "table", t.x, t.y, tW, tH, (s) => {
      return s.add.circle(t.x, t.y, TILE * 0.6, C.wood);
    });
    collisions.push({ x: t.x - tW * 0.4, y: t.y - tH * 0.4, w: tW * 0.8, h: tH * 0.8 });
  }

  // ═══ Bookshelf (2 tiles wide, 2 tiles tall) ═══
  const shelfX = TILE * 2.5;
  const shelfY = ROOM_H - TILE * 3;
  const shelfW = TILE * 2;
  const shelfH = TILE * 2;
  placeSprite(scene, "bookshelf", shelfX, shelfY, shelfW, shelfH, (s) => {
    return s.add.rectangle(shelfX, shelfY, shelfW, shelfH, C.wood);
  });
  collisions.push({ x: shelfX - shelfW / 2, y: shelfY - shelfH / 2, w: shelfW, h: shelfH });
  interactions.bookshelf = {
    x: shelfX,
    y: shelfY + shelfH / 2 + TILE * 0.7,
    radius: TILE * 1.6,
    label: "Browse the bookshelf",
    slug: "bookshelf"
  };

  // ═══ Noticeboard (upper-left, on the wall, 1.5 tiles) ═══
  const noticeX = TILE * 2.5;
  const noticeY = TILE * 3.5;
  const noticeW = TILE * 1.6;
  const noticeH = TILE * 1.6;
  placeSprite(scene, "noticeboard", noticeX, noticeY, noticeW, noticeH, (s) => {
    return s.add.rectangle(noticeX, noticeY, noticeW, noticeH, C.wood);
  });
  collisions.push({ x: noticeX - noticeW / 2, y: noticeY - noticeH / 2, w: noticeW, h: noticeH });
  interactions.noticeboard = {
    x: noticeX,
    y: noticeY + noticeH / 2 + TILE * 0.7,
    radius: TILE * 1.6,
    label: "Read the noticeboard",
    slug: "noticeboard"
  };

  return { collisions, interactions };
}
